import React, { useEffect, useState, useCallback, useRef } from "react";
import { ApplePayConfig } from "services/apple-pay-service.ts";
import { postRequest } from "utils/httpClient.ts";
import { toast } from "react-toastify";

// Define the custom element to prevent TypeScript errors
declare global {
  namespace JSX {
    // noinspection JSUnusedGlobalSymbols
    interface IntrinsicElements {
      "apple-pay-button": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        buttonstyle?: string;
        type?: string;
        locale?: string;
        onClick?: (event: any) => void;
      };
    }
  }

  // Add merchantConfig to the Window interface
  interface Window {
    merchantConfig?: ApplePayConfig;
    ApplePaySession?: typeof ApplePaySession;
  }
}

export enum PaymentStatus {
  SUCCESS,
  FAILURE
}

interface ApplePayButtonProps {
  amount: number;
  onPaymentAuthorized: (applePayToken: string) => Promise<PaymentStatus>;
}

const ApplePayButton = ({ amount, onPaymentAuthorized }: ApplePayButtonProps) => {
  const domainName = window.location.hostname;
  const buttonRef = useRef<HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [merchantConfig, setMerchantConfig] = useState<ApplePayConfig | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const currentSession = useRef<ApplePaySession | null>(null);

  const validateMerchant = useCallback((config: ApplePayConfig) => {
    console.log("[ApplePay] Validating merchant with config:", {
      merchantIdentifier: config.PARTNER_INTERNAL_MERCH_ID,
      displayName: config.DISPLAY_NAME,
      initiative: config.INITIATIVE,
      initiativeContext: config.DOMAIN_LIST[0].DOMAIN_NAME,
      fetchUrl: config.fetchUrl
    });

    const data = {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        merchantIdentifier: config.PARTNER_INTERNAL_MERCH_ID,
        displayName: config.DISPLAY_NAME,
        initiative: config.INITIATIVE,
        initiativeContext: config.DOMAIN_LIST[0].DOMAIN_NAME
      })
    };
    console.log("[ApplePay] Sending validation request to:", config.fetchUrl);
    return fetch(config.fetchUrl, data);
  }, []);

  const handleApplePayClick = useCallback(() => {
    console.log("[ApplePay] Button clicked");
    
    if (isProcessing || !merchantConfig) {
      console.log("[ApplePay] Cannot process click - processing:", isProcessing, "config:", !!merchantConfig);
      return;
    }
    
    if (currentSession.current) {
        console.warn("[ApplePay] Attempting to abort previous session before starting new one.");
        try {
            currentSession.current.abort();
        } catch (e) {
            console.error("[ApplePay] Error aborting previous session:", e);
        }
        currentSession.current = null;
    }

    setIsProcessing(true);
    console.log("[ApplePay] Processing payment for amount:", amount);

    try {
      const ApplePaySession = window.ApplePaySession;
      if (!ApplePaySession) {
        console.error("[ApplePay] ApplePaySession not available");
        toast.error("Apple Pay is not available on this device/browser.");
        setIsProcessing(false);
        return;
      }

      if (!ApplePaySession.canMakePayments()) {
          console.error("[ApplePay] canMakePayments returned false unexpectedly.");
          toast.error("Cannot make Apple Pay payments at this time.");
          setIsProcessing(false);
          return;
      }

      console.log("[ApplePay] Creating payment request with amount:", amount);
      const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
        ...merchantConfig.paymentRequest,
        total: {
          ...merchantConfig.paymentRequest.total,
          label: `Deposit $${amount.toFixed(2)}`,
          amount: amount.toString(),
          type: "final",
        }
      };
      console.log("[ApplePay] Final payment request:", JSON.stringify(paymentRequest, null, 2));

      console.log("[ApplePay] Creating new ApplePaySession");
      const session = new ApplePaySession(3, paymentRequest);
      currentSession.current = session;

      session.onvalidatemerchant = async (event) => {
        console.log("[ApplePay] onvalidatemerchant triggered");
        console.log("[ApplePay] Validation URL:", event.validationURL);

        try {
          const merchantSessionResponse = await validateMerchant(merchantConfig);
          if (!merchantSessionResponse.ok) {
              const errorText = await merchantSessionResponse.text();
              console.error(`[ApplePay] Merchant validation failed: ${merchantSessionResponse.status} ${merchantSessionResponse.statusText}`, errorText);
              throw new Error(`Merchant validation failed: ${merchantSessionResponse.status}`);
          }
          const merchantSession = await merchantSessionResponse.json();
          console.log("[ApplePay] Merchant validation response received:", merchantSession);
          session.completeMerchantValidation(merchantSession);
          console.log("[ApplePay] Merchant validation completed");
        } catch (e) {
          console.error("[ApplePay] Merchant validation failed:", e);
          session.abort();
          setIsProcessing(false);
          currentSession.current = null;
        }
      };

      session.onpaymentauthorized = async (event) => {
        console.log("[ApplePay] Payment authorized event received");
        console.log("[ApplePay] Payment data:", JSON.stringify(event.payment, null, 2));
        
        const applePayToken = event.payment;
        const applePayTokenEncoded = btoa(JSON.stringify(applePayToken));
        console.log("[ApplePay] Token encoded, length:", applePayTokenEncoded.length);

        try {
          const paymentStatus = await onPaymentAuthorized(applePayTokenEncoded);
          console.log("[ApplePay] Payment processing result:", paymentStatus);
          const finalStatus = paymentStatus === PaymentStatus.SUCCESS ? 
            ApplePaySession.STATUS_SUCCESS : ApplePaySession.STATUS_FAILURE;
          console.log("[ApplePay] Completing session with status:", finalStatus);
          session.completePayment({ status: finalStatus });
        } catch (error) {
          console.error("[ApplePay] Payment processing failed:", error);
          console.log("[ApplePay] Completing session with failure due to error in onPaymentAuthorized");
          session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
        } finally {
            console.log("[ApplePay] Payment flow 'onpaymentauthorized' finished.");
            setIsProcessing(false);
            currentSession.current = null;
        }
      };

      session.oncancel = () => {
        console.log("[ApplePay] Session cancelled by user");
        setIsProcessing(false);
        currentSession.current = null;
      };

      console.log("[ApplePay] Beginning Apple Pay session");
      session.begin();
    } catch (error: any) {
        console.error("[ApplePay] Session creation failed:", error);
        if (error instanceof DOMException && error.name === 'InvalidAccessError') {
            console.error("[ApplePay] Detail: InvalidAccessError - potential session conflict or invalid state.");
            toast.error("Could not start Apple Pay. Please ensure no other payment session is active.");
        } else {
             console.error("[ApplePay] Full error object:", JSON.stringify(error, null, 2));
             toast.error("Could not create Apple Pay session. Please try again.");
        }
        setIsProcessing(false);
        currentSession.current = null;
    }
  }, [amount, isProcessing, merchantConfig, onPaymentAuthorized, validateMerchant]);

  useEffect(() => {
    const buttonElement = buttonRef.current;
    if (buttonElement) {
      console.log("[ApplePay] Attaching click listener via ref");
      const clickHandler = (event: Event) => {
          event.preventDefault();
          handleApplePayClick();
      };

      buttonElement.addEventListener('click', clickHandler);

      return () => {
        console.log("[ApplePay] Cleaning up click listener from ref");
        buttonElement.removeEventListener('click', clickHandler);
      };
    } else {
        console.log("[ApplePay] buttonRef.current is null, cannot attach listener yet.");
    }
  }, [handleApplePayClick, buttonRef]);

  useEffect(() => {
    console.log("[ApplePay] Initializing with domain:", domainName);
    console.log("[ApplePay] Current protocol:", window.location.protocol);
    console.log("[ApplePay] ApplePaySession available:", !!window.ApplePaySession);
    
    let isAvailable = false;
    if (window.ApplePaySession) {
        try {
             isAvailable = ApplePaySession.canMakePayments();
             console.log(`[ApplePay] ApplePaySession.canMakePayments() returned: ${isAvailable}`);
        } catch (e) {
            console.error("[ApplePay] Error calling canMakePayments:", e);
            isAvailable = false;
        }
    }

    if (!isAvailable) {
      console.warn("[ApplePay] Apple Pay is not available or enabled in this browser/context.");
      setIsLoading(false);
      setMerchantConfig(null);
      return;
    }

    console.log("[ApplePay] Apple Pay appears available, fetching config...");
    postRequest("/v2/commerce/inovio/apple-pay/get-config", {
      body: { domainName }
    })
      .then(config => {
        console.log("[ApplePay] Received config:", JSON.stringify(config, null, 2));
        if (!config || !config.paymentRequest || !config.PARTNER_INTERNAL_MERCH_ID) {
            console.error("[ApplePay] Received invalid config structure:", config);
            throw new Error("Invalid Apple Pay configuration received from server.");
        }
        window.merchantConfig = config;
        console.log("[ApplePay] merchantConfig set globally on window object");
        setMerchantConfig(config);
        setIsLoading(false);
        console.log("[ApplePay] Button should be enabled and ready.");
      })
      .catch(e => {
        console.error("[ApplePay] Failed to get Apple Pay config:", e);
        toast.error("Failed to load Apple Pay configuration.");
        setIsLoading(false);
        setMerchantConfig(null);
      });
  }, [domainName]);

  if (isLoading) {
    return (
      <div style={{ height: 44, width: '100%', background: '#eee', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '14px' }}>
        Loading Apple Pay...
      </div>
    );
  }

  if (!merchantConfig) {
    return (
      <div style={{ height: 44, width: '100%', background: '#f4f4f4', borderRadius: 4, textAlign: 'center', lineHeight: '44px', fontSize: 14, color: '#666' }}>
        Apple Pay not available
      </div>
    );
  }

  return (
    <div style={{ opacity: isProcessing ? 0.5 : 1 }}>
      <apple-pay-button
        ref={buttonRef}
        buttonstyle="black"
        type="plain"
        locale="en"
        style={{
          pointerEvents: isProcessing ? 'none' : 'auto',
          cursor: isProcessing ? 'not-allowed' : 'pointer'
        }}
      />
      <style>
        {`
          apple-pay-button {
            --apple-pay-button-width: 100%;
            --apple-pay-button-height: 44px;
            --apple-pay-button-border-radius: 4px;
            --apple-pay-button-padding: 0px 0px;
            --apple-pay-button-box-sizing: border-box;
            display: inline-block;
          }
        `}
      </style>
    </div>
  );
};

export default ApplePayButton; 